import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { CheckCircle, ArrowRight, Phone, Mail } from 'lucide-react';
import { Button } from '../components/ui/button';

const fadeUp = {
    initial: { opacity: 0, y: 30 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: 0.6 },
};

const ThankYouPage = () => {
    return (
        <div data-testid="thank-you-page" className="min-h-screen">
            {/* Hero Section */}
            <section className="relative pt-44 pb-24 bg-slate-900 overflow-hidden">
                <div className="absolute inset-0 z-0">
                    <div className="absolute inset-0 bg-gradient-to-br from-slate-900 via-slate-900 to-blue-900/30"></div>
                </div>

                <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 lg:px-24 h-full flex items-center justify-center">
                    <motion.div
                        initial="initial"
                        animate="animate"
                        variants={{
                            animate: { transition: { staggerChildren: 0.1 } },
                        }}
                        className="max-w-3xl text-center"
                    >
                        <motion.div
                            variants={fadeUp}
                            className="mb-6"
                        >
                            <CheckCircle className="w-20 h-20 text-green-400 mx-auto" />
                        </motion.div>

                        <motion.h1
                            variants={fadeUp}
                            className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white leading-tight mb-6"
                            data-testid="thank-you-title"
                        >
                            Thank You For Contacting Vanfrank
                        </motion.h1>

                        <motion.p
                            variants={fadeUp}
                            className="text-lg text-slate-300 leading-relaxed"
                        >
                            We will be in touch with you very shortly.
                        </motion.p>
                    </motion.div>
                </div>
            </section>

            {/* Content Section */}
            <section className="py-16 md:py-24 px-6 md:px-12 lg:px-24">
                <div className="max-w-3xl mx-auto text-center">
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        className="space-y-8"
                    >
                        <div className="bg-slate-50 rounded-2xl p-8 md:p-12">
                            <h2 className="text-2xl font-bold text-slate-900 mb-4">
                                What Happens Next?
                            </h2>
                            <p className="text-slate-600 leading-relaxed mb-6">
                                Our team has received your message and will review it promptly. 
                                A member of our team will reach out to you within 24-48 business hours 
                                to discuss your requirements and how we can help.
                            </p>
                            
                            <div className="border-t border-slate-200 pt-6 mt-6">
                                <p className="text-slate-600 mb-4">
                                    Need immediate assistance? Contact us directly:
                                </p>
                                <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
                                    <a
                                        href="tel:+2342012274476"
                                        className="flex items-center gap-2 text-blue-600 hover:text-blue-700 font-medium"
                                        onClick={() => {
                                            if (window.dataLayer) {
                                                window.dataLayer.push({ event: 'phone_call_click' });
                                            }
                                        }}
                                    >
                                        <Phone size={18} />
                                        +234 201 227 4476
                                    </a>
                                    <span className="hidden sm:inline text-slate-300">|</span>
                                    <a
                                        href="mailto:lagos@vanfranklimited.com"
                                        className="flex items-center gap-2 text-blue-600 hover:text-blue-700 font-medium"
                                        onClick={() => {
                                            if (window.dataLayer) {
                                                window.dataLayer.push({ event: 'email_click' });
                                            }
                                        }}
                                    >
                                        <Mail size={18} />
                                        lagos@vanfranklimited.com
                                    </a>
                                </div>
                            </div>
                        </div>

                        <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
                            <Link to="/">
                                <Button
                                    className="bg-blue-600 hover:bg-blue-700 text-white px-8 py-3 rounded-full"
                                >
                                    Return to Home
                                    <ArrowRight className="ml-2" size={18} />
                                </Button>
                            </Link>
                            <Link to="/services">
                                <Button
                                    variant="outline"
                                    className="border-slate-300 text-slate-700 hover:bg-slate-50 px-8 py-3 rounded-full"
                                >
                                    Explore Our Solutions
                                </Button>
                            </Link>
                        </div>
                    </motion.div>
                </div>
            </section>
        </div>
    );
};

export default ThankYouPage;
